import { reactive, watch } from 'vue'
import { api } from '../api.js'
import { session } from '../session.js'

function emptyState() {
  return { templates: [], favoriteIds: [], loaded: false, refreshing: false,
    error: null, favoritesUnavailable: false, pendingFavoriteId: '', searchQuery: '', currentPage: 1 }
}

export function createReportCatalogState({ request = api } = {}) {
  const state = reactive({ scope: '', ...emptyState() })
  let generation = 0, pending = null, readController = null, mutationController = null, favoriteRevision = 0

  function setScope(scope) {
    if (state.scope === scope) return
    generation++
    readController?.abort()
    mutationController?.abort()
    readController = null; mutationController = null; pending = null; favoriteRevision = 0
    Object.assign(state, emptyState(), { scope })
  }

  function refresh() {
    if (!state.scope) return Promise.resolve(null)
    if (pending) return pending
    const token = generation, revision = favoriteRevision, controller = new AbortController()
    readController = controller
    state.refreshing = true
    state.error = null
    const options = { signal: controller.signal }
    pending = Promise.allSettled([request('/catalog', options), request('/favorites', options)]).then(([catalog, favorites]) => {
      if (token !== generation) return null
      if (catalog.status === 'rejected' || !Array.isArray(catalog.value?.items)) {
        Object.assign(state, { loaded: false, templates: [], favoriteIds: [], favoritesUnavailable: false,
          error: catalog.reason ?? new Error('Invalid catalog response') })
        return null
      }
      state.templates = catalog.value.items
      state.loaded = true
      if (revision === favoriteRevision && !state.pendingFavoriteId) {
        const valid = favorites.status === 'fulfilled' && Array.isArray(favorites.value?.templateIds)
        state.favoriteIds = valid ? favorites.value.templateIds : []
        state.favoritesUnavailable = !valid
      }
      return state
    }).finally(() => {
      if (token === generation) {
        state.refreshing = false
        readController = null; pending = null
      }
    })
    return pending
  }

  async function toggleFavorite(templateId) {
    if (!state.scope || state.pendingFavoriteId || !state.loaded) return null
    const token = generation, controller = new AbortController(), removing = state.favoriteIds.includes(templateId)
    favoriteRevision++
    mutationController = controller
    state.pendingFavoriteId = templateId
    try {
      await request(`/favorites/${templateId}`, { method: removing ? 'DELETE' : 'POST', signal: controller.signal })
      if (token !== generation) return null
      state.favoriteIds = removing ? state.favoriteIds.filter((id) => id !== templateId) : [...state.favoriteIds, templateId]
      state.favoritesUnavailable = false
      state.currentPage = 1
      return state
    } catch (error) {
      if (token === generation) throw error
      return null
    } finally {
      if (token === generation) {
        favoriteRevision++
        state.pendingFavoriteId = ''
        mutationController = null
      }
    }
  }

  return { state, setScope, refresh, toggleFavorite }
}

export function reportCatalogScope(user) {
  return user ? JSON.stringify([user.id ?? user.username, user.accessVersion, user.role,
    user.effectivePermissions, user.reportLanguage ?? 'uk']) : ''
}

export const reportCatalog = createReportCatalogState()
watch(() => reportCatalogScope(session.user), (scope) => reportCatalog.setScope(scope), { immediate: true, flush: 'sync' })
